import React from 'react';
import { PageHeader, Card, CardContent, Button, Input, Alert } from '@/components/ui';
import { AuthService } from '@/services/AuthService';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { Logo } from '../ui/Logo';

export function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const search = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = search.get('token') || '';

  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [done, setDone] = React.useState(false);
  const svc = React.useMemo(() => new AuthService(), []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('Missing reset token');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      await svc.resetPassword(token, password);
      setDone(true);
      // Redirect to sign-in after a brief pause
      setTimeout(() => navigate('/auth', { replace: true }), 1200);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block hover:opacity-80 transition-opacity">
            <Logo size="xl" className="justify-center mb-2" />
            <p className="text-sm text-muted-foreground mt-1">Artistic Intelligence</p>
          </Link>
        </div>

        <div className="bg-background rounded-2xl shadow-xl overflow-hidden border">
          <div className="p-6 md:p-8">
            <h2 className="text-xl font-semibold mb-2">Reset your password</h2>
            {!token && (
              <Alert variant="error" className="mb-4">Invalid reset link. Please check the URL.</Alert>
            )}
            {error && (
              <Alert variant="error" className="mb-4">{error}</Alert>
            )}
            {done && (
              <Alert variant="success" className="mb-4">
                Your password has been reset. <Link to="/auth" className="underline">Sign in</Link>.
              </Alert>
            )}

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Password requirements</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• At least 12 characters</li>
                <li>• One lowercase, one uppercase letter</li>
                <li>• One number and one symbol</li>
              </ul>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <Input
                label="New password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                required
              />
              <Input
                label="Confirm new password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••••"
                required
                error={confirm && password !== confirm ? 'Passwords do not match' : undefined}
              />

              <Button type="submit" variant="primary" className="w-full" loading={isLoading} disabled={!token}>
                Update password
              </Button>
            </form>
          </div>
        </div>

        <div className="mt-8 text-center text-xs text-muted-foreground">
          Having trouble? Contact support.
        </div>
      </div>
    </div>
  );
}

